// Shared shape + normalizer for the Home Activity numbers.
// Used by the seller-facing /activity page and the admin Activity tab so both
// always agree on the data shape, including older saved records.

export const STAT_FIELDS = [
  { key: "social_views", label: "Social views" },
  {
    key: "listing_views",
    label: "Listing views",
    // Genuinely automatic, and the only portal-style number that is: real
    // visits to this listing's own PorchLight page, counted server-side.
    // Deduped per visitor per day, and link-preview scrapers are excluded —
    // so it's people, not Facebook fetching a card. Portal numbers (Zillow,
    // Redfin) stay manual; see docs/BACKLOG.md.
    auto: "Counted from your listing page",
    autoVerb: "count it for me",
  },
  { key: "engagements", label: "Engagements" },
  {
    key: "showings_count",
    label: "Showings",
    // Genuinely automatic: every showing you book is already a row on the
    // calendar, so this is just counting them.
    auto: "Counted from your showings calendar",
    autoVerb: "count it for me",
  },
  {
    key: "open_house_groups",
    label: "Open house groups",
    // NOT automatic, and the copy must not pretend otherwise: you still tally
    // groups at the door and type them on the open house entry. What we save
    // you is keeping a second running total in sync by hand.
    auto: "Added up from the group counts on your open house entries",
    autoVerb: "add them up for me",
  },
];

// Stats the backend keeps in sync from db.showings. They carry an extra `auto`
// flag. For the two showings-derived keys it is always on (see
// LOCKED_STAT_KEYS below); listing_views can still be typed by hand.
export const AUTO_STAT_KEYS = STAT_FIELDS.filter((f) => f.auto).map((f) => f.key);

export const isAutoStat = (key) => AUTO_STAT_KEYS.includes(key);

// These two are not a figure anyone forms an opinion about: they are a count
// of the rows on the showings calendar. Locking them is what keeps the tile
// on the seller's page from disagreeing with the list printed underneath it.
export const LOCKED_STAT_KEYS = ["showings_count", "open_house_groups"];
export const isLockedStat = (key) => LOCKED_STAT_KEYS.includes(key);

// Sources every new listing starts with. The agent can rename, reorder by
// deleting/re-adding, switch off, or remove any of these — and add their own.
export const DEFAULT_PLATFORMS = [
  { id: "website", label: "Listing Website", views: 0, metric_label: "Leads", metric_value: 0, enabled: true },
  { id: "zillow", label: "Zillow", views: 0, metric_label: "Saves", metric_value: 0, enabled: true },
  { id: "redfin", label: "Redfin", views: 0, metric_label: "Saves", metric_value: 0, enabled: true },
  { id: "realtor", label: "Realtor.com", views: 0, metric_label: "Saves", metric_value: 0, enabled: true },
];

const DEFAULT_STATS = STAT_FIELDS.reduce(
  (acc, f) => ({
    ...acc,
    [f.key]: { value: 0, enabled: true, ...(f.auto ? { auto: true } : {}) },
  }),
  {}
);

function num(v) {
  const n = parseInt(v, 10);
  return Number.isFinite(n) && n > 0 ? n : 0;
}

function normalizePlatformRow(row, fallbackIndex) {
  return {
    id: row?.id || `src_${fallbackIndex}`,
    label: typeof row?.label === "string" ? row.label : "",
    views: num(row?.views),
    // Older records stored the second number under `leads` or `saves`.
    metric_label: row?.metric_label || (row?.leads !== undefined ? "Leads" : "Saves"),
    metric_value: num(row?.metric_value ?? row?.leads ?? row?.saves),
    enabled: row?.enabled !== false,
  };
}

/** Accepts the current array shape, the earlier object-keyed shape, or nothing. */
export function normalizePlatforms(raw) {
  if (Array.isArray(raw)) {
    return raw.map(normalizePlatformRow);
  }
  if (raw && typeof raw === "object") {
    // Legacy: { website: {...}, zillow: {...}, ... }
    return DEFAULT_PLATFORMS.map((d) =>
      raw[d.id] ? { ...normalizePlatformRow({ ...raw[d.id], id: d.id, label: d.label }, d.id), metric_label: d.metric_label } : { ...d }
    );
  }
  return DEFAULT_PLATFORMS.map((d) => ({ ...d }));
}

/** Full normalize of a personalization record's `activity_stats`. */
export function normalizeActivityStats(raw) {
  const src = raw || {};
  const stats = {};
  STAT_FIELDS.forEach((f) => {
    const cur = src[f.key];
    // Very old records stored a bare number instead of { value, enabled }.
    const isNumber = typeof cur === "number";
    stats[f.key] = {
      value: num(isNumber ? cur : cur?.value),
      enabled: isNumber ? true : cur?.enabled !== false,
    };
    if (f.auto) {
      // Records saved before auto-counting existed have no flag and opt in.
      stats[f.key].auto = isNumber ? true : cur?.auto !== false;
      // A record switched to manual before these were locked still reads back
      // as automatic, so an old typed-in number cannot outrank the calendar.
      if (isLockedStat(f.key)) stats[f.key].auto = true;
    }
  });
  return { ...DEFAULT_STATS, ...stats, platforms: normalizePlatforms(src.platforms) };
}

export function newPlatformRow(seed) {
  return {
    id: `src_${seed}`,
    label: "",
    views: 0,
    metric_label: "Saves",
    metric_value: 0,
    enabled: true,
  };
}

export function formatStatNumber(n) {
  const v = Number(n) || 0;
  if (v >= 1000) return `${(v / 1000).toFixed(v % 1000 === 0 ? 0 : 1)}K`;
  return String(v);
}
